import Model from '@ember-data/model';
import { inject as service } from '@ember/service';
import { or, and, not } from '@ember/object/computed';
import { computed } from '@ember/object';
import { isBlank } from '@ember/utils';
import attr from 'ember-data/attr';
import DS from 'ember-data';
import { modelApiAction } from 'cio/utils/model-api-actions';

export interface DnsDomainObject {
  type: string;
  domain: string;
  value: string;
  name: string;
}

export type DomainPlatform = 'mailgun' | 'sendgrid';

export type IPPoolPendingOptions = 'shared_requested' | 'transactional_requested' | 'dedicated_requested';
export type IPPoolConfirmedOptions = 'shared' | 'transactional' | 'dedicated' | 'custom';
export type IPPoolOptions = IPPoolConfirmedOptions | IPPoolPendingOptions;

const IPPoolDisplayNames: Record<IPPoolOptions, string> = {
  shared: 'Shared IP Pool - Default',
  shared_requested: 'Shared IP Pool - Default',
  dedicated: 'Dedicated IP Pool',
  dedicated_requested: 'Dedicated IP Pool',
  transactional: 'Transactional IP Pool',
  transactional_requested: 'Transactional IP Pool',
  custom: 'Custom SMTP Server',
} as const;

export default class DomainModel extends Model {
  @service store!: DS.Store;

  @attr('string', { defaultValue: '' }) domain!: string;

  get lowercasedDomain() {
    return this.domain.toLowerCase();
  }

  @attr('string') cname!: string;
  @attr('string') parentDomain!: string;

  // domain is verified when checks necessary for e-mail sending are passing
  // the value is calculated on the back-end based on DKIM, SPF, domain
  // ownership records
  @attr('boolean') verified!: boolean;

  @attr('boolean') verifiedCname!: boolean;
  @attr('boolean') verifiedDkim!: boolean;
  @attr('boolean') verifiedDomain!: boolean;
  @attr('boolean') verifiedHttp!: boolean;
  @attr('boolean') verifiedHttps!: boolean;
  @attr('boolean') verifiedSpf!: boolean;
  @attr('boolean') verifiedHsts!: boolean;
  @attr('boolean', { allowNull: true }) verifiedDmarc!: boolean;

  @attr('string') ipPool!: IPPoolOptions;

  @attr('array', { defaultValue: () => [] }) dnsRecords!: DnsDomainObject[];

  @not('verified') notVerified!: boolean;

  // domain is fully verified when all checks are passing: used to hide "re-check" button
  @and('verifiedCname', 'verifiedDkim', 'verifiedDomain', 'verifiedHttps', 'verifiedSpf')
  fullyVerified!: boolean;

  @or('verifiedHttp', 'verifiedHttps')
  verifiedHttpOrHttps!: boolean;

  @computed('cname', 'domain')
  get cnameOrFallback() {
    let { cname } = this;

    if (isBlank(cname)) {
      cname = `email.${this.domain}`;
    }

    return cname;
  }

  @computed('dnsRecords.[]')
  get hasOwnershipRecord() {
    return this.dnsRecords.some((record) => record.name === 'Ownership' || record.name === 'Domain');
  }

  @computed('ipPool')
  get ipPoolDisplayName() {
    return IPPoolDisplayNames[this.ipPool];
  }

  @computed('ipPool')
  get isUsingTransactionalIpPool() {
    return ['transactional', 'transactional_requested'].includes(this.ipPool);
  }

  @computed('ipPool')
  get ipPoolPending() {
    return ['shared_requested', 'transactional_requested', 'dedicated_requested'].includes(this.ipPool);
  }

  requestIpPool = modelApiAction({
    type: 'POST',
    path: 'ip_pool',

    after(this: DomainModel, response) {
      return this.store.pushPayload(response);
    },
  });
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    domain: DomainModel;
  }
}
